import React, { useState } from 'react';
import { Radio, AlertCircle, Calendar, MapPin, ChevronLeft } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { isScorer } from '../../tournaments/access';
import NameBlock from './lists/NameBlock';
import { STATUS_BADGES } from './lists/MatchRow';
import StartScoringConfirmModal from './StartScoringConfirmModal';

/**
 * Pure match-detail content — round/group/status, schedule/court, the two
 * competing sides, and the Start Scoring / View Live Game action. Extracted
 * out of MatchDetailModal.jsx so FullscreenScheduleOverlay.jsx's Previous/
 * Next can show the exact same rich detail view inline (no modal chrome —
 * it's already full-screen) instead of a bare "Start Scoring?" prompt with
 * no context, which was a second, worse reimplementation of the same thing.
 * MatchDetailModal now just wraps this in its dialog chrome (backdrop,
 * header, Previous/Next footer) — this component owns none of that.
 *
 * `row.status` is read defensively (`row.status || row.liveData?.checkin_status
 * || row.liveData?.status`) rather than trusted outright: MatchTable.jsx's
 * caller always pre-hydrates it via scheduleRows.js's resolveOverrides, but
 * a caller working off a raw, not-yet-hydrated row (as fullscreen's
 * Previous/Next briefly did before this fix — see GeneratedScheduleStep.jsx)
 * would otherwise silently see `undefined` and always fall into the
 * "not started" branch, even for an already-completed match.
 *
 * Reaching this screen (via Previous/Next or the schedule list) takes no
 * click of its own — it's just the row's real state. Start Scoring then
 * opens the exact same confirm used by the schedule list's own row badge
 * (StartScoringConfirmModal.jsx) rather than a second bespoke screen, so the
 * confirmation step looks and behaves identically everywhere a user is about
 * to claim a match.
 *
 * `onBack` — optional; only FullscreenScheduleOverlay passes it (as
 * `backToList`). Mirrors GameComponent's own persistent "< Back to Schedule"
 * button (see LiveGameTab.jsx's `backLabel`/`onCancel`) so leaving this
 * pre-score screen doesn't require reaching for the fixed header above —
 * this screen was the one place in fullscreen's game flow without that
 * inline affordance, unlike GameComponent's once scoring actually starts.
 * MatchDetailModal.jsx's usage leaves this unset — it already has its own
 * dialog close (X) chrome, so nothing renders there.
 */
export const formatEventSchedule = (event) => {
  if (!event?.start_time) return null;
  const start = new Date(event.start_time);
  const end = event.end_time ? new Date(event.end_time) : null;
  const dateStr = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const startStr = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const endStr = end ? end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : null;
  return `${dateStr} · ${endStr ? `${startStr} – ${endStr}` : startStr}`;
};

const MatchDetailPanel = ({ row, tournamentVO, currentUserId, onStartScoring, onOpenLive, onBack }) => {
  const { theme } = useTheme();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const status = row.status || row.liveData?.checkin_status || row.liveData?.status || 'draft';
  const statusInfo = STATUS_BADGES[status] || STATUS_BADGES.draft;
  const court = row.court || row.liveData?.court || null;
  const scheduleText = formatEventSchedule(row.liveData?.event);
  const venueText = row.liveData?.event?.venue?.fullAddress || row.liveData?.event?.venue?.name || null;

  const isThisUserTheScorer = isScorer(currentUserId, tournamentVO);
  const tournamentStarted = tournamentVO?.status === 'in_progress';
  const canStart = !!row.liveData && isThisUserTheScorer && tournamentStarted && status !== 'completed';
  const startDisabledReason = row.liveData && !canStart
    ? (!isThisUserTheScorer
        ? 'Only the assigned scorer can start this match'
        : !tournamentStarted
          ? 'Tournament must be started first'
          : 'This match is already completed')
    : '';

  const canOpenLiveGame = (status === 'live' || status === 'completed') && !!row.liveData && !!onOpenLive;

  return (
    <div className="px-6 py-5">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className={`mb-4 flex items-center gap-1 rounded-lg p-1.5 -ml-1.5 ${theme.textMuted} hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
          title="Back to Schedule"
        >
          <ChevronLeft size={20} />
          <span className="text-sm font-medium hidden sm:inline">Back to Schedule</span>
        </button>
      )}
      {(row.number != null || row.gameTypeLabel || row.roundLabel) && (
        <div className={`mb-4 text-xs ${theme.textMuted}`}>
          {row.number != null && `#${row.number}`}
          {(row.gameTypeLabel || row.roundLabel) && `${row.number != null ? ' · ' : ''}${row.gameTypeLabel || row.roundLabel}`}
          {row.groupLabel && ` · ${row.groupLabel}`}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${statusInfo.className}`}>
          {statusInfo.label}
        </span>
      </div>

      {/* One consolidated logistics block — when/where this match is
          actually happening — rather than splitting court off into its own
          bare pill: a Calendar row for date/time (only once the calendar
          icon has actually set one) and a MapPin row for court + full venue
          address (only once courts are configured / an address is set).
          Either can be present without the other. */}
      {(scheduleText || court || venueText) && (
        <div className={`mb-4 space-y-2 rounded-xl border ${theme.border} p-3`}>
          {scheduleText && (
            <div className={`flex items-start gap-2 text-sm ${theme.textSecondary}`}>
              <Calendar size={15} className={`mt-0.5 shrink-0 ${theme.textMuted}`} />
              <span>{scheduleText}</span>
            </div>
          )}
          {(court || venueText) && (
            <div className={`flex items-start gap-2 text-sm ${theme.textSecondary}`}>
              <MapPin size={15} className={`mt-0.5 shrink-0 ${theme.textMuted}`} />
              <div>
                {court && <div>{court}</div>}
                {venueText && <div className={`text-xs ${theme.textMuted}`}>{venueText}</div>}
              </div>
            </div>
          )}
        </div>
      )}

      <div className={`rounded-xl border ${theme.border} p-4`}>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <NameBlock entry={row.slotA} placeholder={row.placeholderA} players={row.playersA} align="text-right" />
          <span className={`text-xs font-semibold ${theme.textMuted}`}>vs</span>
          <NameBlock entry={row.slotB} placeholder={row.placeholderB} players={row.playersB} align="text-left" />
        </div>
      </div>

      {row.liveData ? (
        canOpenLiveGame ? (
          <button
            type="button"
            onClick={() => onOpenLive(row)}
            className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]`}
          >
            <Radio size={15} />
            {status === 'live' ? 'View Live Game' : 'View Score'}
          </button>
        ) : (
          <>
            {canStart && (
              <p className={`mt-4 text-sm ${theme.textSecondary}`}>
                You will be the sole scorer for this match. Other users will see a read-only view until the game is finished.
              </p>
            )}
            <button
              type="button"
              onClick={() => canStart && setConfirmOpen(true)}
              disabled={!canStart}
              title={canStart ? 'Start scoring this match' : startDisabledReason}
              className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                canStart ? `${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]` : `${theme.buttonGhost} cursor-not-allowed opacity-60`
              }`}
            >
              <Radio size={15} />
              Start Scoring
              {!canStart && startDisabledReason && (
                <span className="flex items-center gap-1 text-xs opacity-90">
                  <AlertCircle size={12} />
                  {startDisabledReason}
                </span>
              )}
            </button>
            <StartScoringConfirmModal
              isOpen={confirmOpen}
              onClose={() => setConfirmOpen(false)}
              row={row}
              isStarting={isStarting}
              onConfirm={async () => {
                setIsStarting(true);
                try {
                  await onStartScoring(row);
                  setConfirmOpen(false);
                } finally {
                  setIsStarting(false);
                }
              }}
            />
          </>
        )
      ) : (
        <div className={`mt-4 rounded-xl border border-dashed ${theme.border} p-4 text-center text-xs ${theme.textMuted}`}>
          Save this schedule to enable check-in and scoring.
        </div>
      )}
    </div>
  );
};

export default MatchDetailPanel;
